import { Box, Checkbox, Divider, Flex, Group, Title, UnstyledButton } from '@mantine/core';
/* eslint-disable max-statements */
import { useEffect, useState } from 'react';

import { Declaration, DeclarationGoods, IRiskAnalysisPriceNewValues } from '@e-origin/shared';

import { InputsComparator } from '@components';
import { RISK_RATE_PERCENTAGE_FACTOR } from '@constants';
import { FormatValueTypes } from '@enums';
import { formatValue } from '@utils';
import { riskAnalysisService } from '../../../services';
import { ValidateAllStateEnum } from '../RiskAnalysis';
import { ManuallySetPrice } from './ManuallySetPrice';
import classes from './RiskAnalysisPrice.module.css';

interface RiskAnalysisPriceProps {
  goodDetails: DeclarationGoods;
  declaration: Declaration;
  update: (obj: IRiskAnalysisPriceNewValues) => void;
  validate: ValidateAllStateEnum;
}

export const RiskAnalysisPrice: React.FC<RiskAnalysisPriceProps> = ({ declaration, goodDetails, update, validate }) => {
  const [newPriceSaved, setNewPriceSaved] = useState(null);
  const [validatedValueCheck, setValidatedValueCheck] = useState(!!goodDetails.evalMethodWebValue?.validatedValueCheck);
  const INVOICED_VALUE_PLACEHOLDER = 'Invoiced value';

  const validatedValuesCheckDisabled = riskAnalysisService.evalMethodWebValueCheckDisabled(goodDetails);

  const evalMethodWebInvoicedValue = formatValue(goodDetails?.evalMethodWebValue?.invoicedAmountPerKg || 0, FormatValueTypes.VALUE);

  const invoicedItemValue = formatValue(goodDetails.itemAmountInvoiced / goodDetails.exchangeRate || 0, FormatValueTypes.TOTAL_VALUE);

  const [openSetPrice, setOpenSetPrice] = useState(false);

  const [invoicedRateOk, setInvoicedRateOk] = useState(goodDetails.evalMethodWebValue?.invoicedRateOk);
  const [invoicedRateValue, setInvoicedRateValue] = useState(goodDetails.evalMethodWebValue?.invoicedRate || 0);
  const evalMethodStatisticalRateLowestOk =
    goodDetails.evalMethodAiHsCode.suggestedHsCode && goodDetails.evalMethodStatisticalValue?.suggestedLowest
      ? goodDetails.evalMethodStatisticalValue?.suggestedLowest?.madbInvoicedRateOk
      : goodDetails.evalMethodStatisticalValue?.lowest?.madbInvoicedRateOk;
  const evalMethodStatisticalRateHighestOk =
    goodDetails.evalMethodAiHsCode.suggestedHsCode && goodDetails.evalMethodStatisticalValue?.suggestedHighest
      ? goodDetails.evalMethodStatisticalValue?.suggestedHighest?.madbInvoicedRateOk
      : goodDetails.evalMethodStatisticalValue?.highest?.madbInvoicedRateOk;

  const isEvalMethodStatisticalLowestValueExist =
    goodDetails.evalMethodAiHsCode.suggestedHsCode && goodDetails.evalMethodStatisticalValue?.suggestedLowest
      ? !!goodDetails.evalMethodStatisticalValue?.suggestedLowest
      : !!goodDetails.evalMethodStatisticalValue?.lowest;
  const isEvalMethodStatisticalHighestValueExist =
    goodDetails.evalMethodAiHsCode.suggestedHsCode && goodDetails.evalMethodStatisticalValue?.suggestedHighest
      ? !!goodDetails.evalMethodStatisticalValue?.suggestedHighest
      : !!goodDetails.evalMethodStatisticalValue?.highest;
  const isOnlyEvalMethodStatisticalLowestValueExist = isEvalMethodStatisticalLowestValueExist && isEvalMethodStatisticalHighestValueExist;

  const IMADB_VALUE = `IMADB value for
    ${goodDetails.evalMethodAiHsCode.suggestedHsCode || goodDetails.commodityCode.hsCode}`;

  const evalMethodStatisticalLowestValue = isOnlyEvalMethodStatisticalLowestValueExist ? 'EvalMethod Statistical Value' : 'EvalMethod Statistical Lowest Value';

  const evalMethodStatisticalIMADBLowestValue = formatValue(
    (goodDetails.evalMethodAiHsCode.suggestedHsCode && isEvalMethodStatisticalLowestValueExist
      ? goodDetails.evalMethodStatisticalValue?.suggestedLowest?.madbAmountPerKg
      : goodDetails.evalMethodStatisticalValue?.lowest?.madbAmountPerKg) || 0,
    FormatValueTypes.VALUE,
  );

  const evalMethodStatisticalLowestInvoicedRate = formatValue(
    (goodDetails.evalMethodAiHsCode.suggestedHsCode && isEvalMethodStatisticalLowestValueExist
      ? goodDetails.evalMethodStatisticalValue?.suggestedLowest?.madbInvoicedRate
      : goodDetails.evalMethodStatisticalValue?.lowest?.madbInvoicedRate) || 0,
    FormatValueTypes.RATE,
  );

  const evalMethodStatisticalIMADBHighestValue = formatValue(
    (goodDetails.evalMethodAiHsCode.suggestedHsCode &&
    isEvalMethodStatisticalHighestValueExist &&
    goodDetails.evalMethodStatisticalValue?.suggestedHighest?.madbAmountPerKg
      ? goodDetails.evalMethodStatisticalValue?.suggestedHighest?.madbAmountPerKg
      : goodDetails.evalMethodStatisticalValue?.highest?.madbAmountPerKg) || 0,
    FormatValueTypes.VALUE,
  );

  const evalMethodStatisticalHighestInvoicedRate = formatValue(
    (goodDetails.evalMethodAiHsCode.suggestedHsCode && isEvalMethodStatisticalHighestValueExist
      ? goodDetails.evalMethodStatisticalValue?.suggestedHighest?.madbInvoicedRate
      : goodDetails.evalMethodStatisticalValue?.highest?.madbInvoicedRate) || 0,
    FormatValueTypes.RATE,
  );

  useEffect(() => {
    setValidatedValueCheck(!!goodDetails.evalMethodWebValue?.validatedValueCheck);
    setNewPriceSaved(null);
    setInvoicedRateOk(goodDetails.evalMethodWebValue?.invoicedRateOk);
    setInvoicedRateValue(goodDetails.evalMethodWebValue?.invoicedRate || 0);
  }, [goodDetails]);

  useEffect(() => {
    if (validate === null || validate === ValidateAllStateEnum.SOME_SELECTED) {
      return;
    }
    setValidatedValueCheck(validate === ValidateAllStateEnum.ALL_SELECTED && !validatedValuesCheckDisabled);
  }, [validate]);

  useEffect(() => {
    update({
      invoicedRate: invoicedRateValue,
      invoicedRateOk,
      newPriceSaved,
      validatedValueCheck,
      validatedValuesCheckDisabled,
    });
  }, [validatedValueCheck, invoicedRateValue, invoicedRateOk]);

  const handleNewPrice = (newPrice: number) => {
    setNewPriceSaved(newPrice);
    setOpenSetPrice(false);
  };

  return (
    <>
      <>
        <Group justify="space-between" align="center" px="lg">
          <Title order={6} fw={600}>
            Values
          </Title>

          <Checkbox
            checked={validatedValueCheck}
            onChange={(event) => setValidatedValueCheck(event.currentTarget.checked)}
            disabled={validatedValuesCheckDisabled}
            label="Validate the values"
          />
        </Group>

        <Divider mt="sm" mb="lg" />

        <Box px="lg">
          <InputsComparator
            data-pw="evalmethod-web-value"
            title="EvalMethod Web Value"
            placeholders={['Invoiced item value', 'Web value']}
            values={[invoicedItemValue, formatValue(newPriceSaved || goodDetails.webScraping.webItemAmountResale || 0, FormatValueTypes.TOTAL_VALUE)]}
            badge={{
              status: invoicedRateOk ? 'success' : 'error',
              text: (
                <div>
                  <div>
                    Calculated rate
                    {formatValue(goodDetails.evalMethodWebValue.invoicedRate * RISK_RATE_PERCENTAGE_FACTOR || 0, FormatValueTypes.PERCENTAGE)}%
                  </div>
                  {!invoicedRateOk && (
                    <div>
                      Reasonable Value {goodDetails.suggestedValues?.unitValue}€{'  '}
                      {formatValue(goodDetails.evalMethodWebValue.webValueRate * RISK_RATE_PERCENTAGE_FACTOR || 0, FormatValueTypes.PERCENTAGE)}%
                    </div>
                  )}
                </div>
              ),
            }}
            disabled={[true, true]}
          />

          <Flex justify={'flex-end'} w={'100%'}>
            <UnstyledButton c={'#0F6298'} data-pw="manually-set-price-btn" onClick={() => setOpenSetPrice(true)} className={classes.setPriceButton}>
              Manually set the price
            </UnstyledButton>
          </Flex>

          {!isEvalMethodStatisticalLowestValueExist && !isEvalMethodStatisticalHighestValueExist && (
            <>
              <Divider />
              <InputsComparator
                title="EvalMethod Statistical Value"
                placeholders={[INVOICED_VALUE_PLACEHOLDER, IMADB_VALUE]}
                values={[evalMethodWebInvoicedValue, formatValue(undefined, FormatValueTypes.VALUE)]}
                disabled={[true, true]}
              />
            </>
          )}

          {isEvalMethodStatisticalLowestValueExist && (
            <>
              <Divider my="md" />
              <InputsComparator
                title={evalMethodStatisticalLowestValue}
                placeholders={[INVOICED_VALUE_PLACEHOLDER, IMADB_VALUE]}
                values={[evalMethodWebInvoicedValue, evalMethodStatisticalIMADBLowestValue]}
                badge={{
                  status: evalMethodStatisticalRateLowestOk ? 'success' : 'error',
                  text: `Calculated rate ${evalMethodStatisticalLowestInvoicedRate}`,
                }}
                disabled={[true, true]}
              />
            </>
          )}

          {isEvalMethodStatisticalHighestValueExist && (
            <>
              <Divider my="md" />
              <InputsComparator
                title="EvalMethod Statistical Highest Value"
                placeholders={[INVOICED_VALUE_PLACEHOLDER, IMADB_VALUE]}
                values={[evalMethodWebInvoicedValue, evalMethodStatisticalIMADBHighestValue]}
                badge={{
                  status: evalMethodStatisticalRateHighestOk ? 'success' : 'error',
                  text: `Calculated rate ${evalMethodStatisticalHighestInvoicedRate}`,
                }}
                disabled={[true, true]}
              />
            </>
          )}
        </Box>
      </>
      <ManuallySetPrice data-pw="manually-set-price" opened={openSetPrice} onHide={handleNewPrice} declaration={declaration} goodDetails={goodDetails} />
    </>
  );
};
